import type { ColumnFilterCategory, ColumnHeaderFilter } from '../types';

/**
 * Logika operatorów wiersza filtrów (DevExtreme-style) — CZYSTA, bez React.
 *
 * Wiersz filtrów NIE pozwala WYBRAĆ operatora (od 2026-09-15 nie ma menu operatorów —
 * `FilterRowInputControl`): komórka text/number/date filtruje domyślnym operatorem kategorii
 * (`getDefaultOperator`: tekst „zawiera”, liczba / data „równa się”). Operator ustawiony w oknie
 * nagłówka wiersz odczytuje (`readOperatorFromFilter`) i ZACHOWUJE przy edycji wartości
 * (`useFilterRowDraft`: „pomiędzy” → para od–do, operatory bez wartości → brak pola; zmiana
 * wartości emituje filtr z tym samym operatorem). Zestaw tekstowy służy dziś kolumnom złożonym
 * (`COMPOSITE_TEXT_FILTER_OPERATORS` → zakładka „Warunek” popupu); `getRowOperators` (poza testami
 * bez wywołań) i wzmianki o „menu wiersza” w komentarzach poniżej to pozostałość dawnego projektu
 * (DevExtreme).
 * Operator „pomiędzy" (`between`) NIE jest operatorem backendu — mapuje się na
 * `mode:'range'` (rangeMin/rangeMax → gte/lte po stronie serwera i lokalnego
 * `passesHeaderFilter`). Pozostałe operatory → `mode:'condition'`. Kształt
 * emitowanego `ColumnHeaderFilter` jest 1:1 z popupem nagłówka
 * (`ColumnHeaderFilterPopup.handleApply`) — oba pisza do tego samego stanu
 * `headerFilters[columnId]`, więc muszą produkować identyczny obiekt.
 *
 * Zestawy operatorów i kolejność wzorowane na DevExtreme React DataGrid (filter
 * row): liczby i daty dzielą JEDEN zestaw (eq/neq/lt/gt/lte/gte/between),
 * tekst ma własny (contains/not_contains/starts_with/ends_with/eq/neq).
 * Operatory bez wartości (`is_null`/`is_not_null`) są zdefiniowane dla
 * kompletności i parytetu z popupem, ale NIE trafiają do menu wiersza
 * (DevExtreme ich tam nie pokazuje — dostępne pozostają przez popup nagłówka).
 */

export type RowFilterOperator =
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_null'
  | 'is_not_null';

/** Kategorie kolumn, które dostają menu operatorów w wierszu (reszta ma własne kontrolki). */
export type MenuFilterCategory = 'text' | 'number' | 'date';

const TEXT_OPERATORS: RowFilterOperator[] = [
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'eq',
  'neq',
];

/** Wspólny zestaw dla liczb i dat — kolejność jak w DevExtreme filter row. */
const ORDINAL_OPERATORS: RowFilterOperator[] = ['eq', 'neq', 'lt', 'gt', 'lte', 'gte', 'between'];

/**
 * Operatory tekstowe dla kolumny ZŁOŻONEJ (grupa OR, np. „Imię i nazwisko") — 6 operatorów
 * tekstowych BEZ „jest puste"/„nie jest puste" (dwuznaczne dla nazwy złożonej z kilku pól; backend
 * i tak je pomija — patrz `buildOrGroupSQL`). Ustawione jako `headerFilter.operators` zawężają też
 * zakładkę „Warunek" popupu nagłówka (`ColumnHeaderFilterPopup` respektuje `filterConfig.operators`).
 */
export const COMPOSITE_TEXT_FILTER_OPERATORS = [...TEXT_OPERATORS];

/** Operatory bez pola wartości — nie w menu wiersza, ale rozpoznawane przy budowie filtra. */
export const NO_VALUE_OPERATORS: readonly RowFilterOperator[] = ['is_null', 'is_not_null'];

/**
 * Operatory dostępne w menu wiersza dla danej kategorii. Kategorie spoza
 * text/number/date (select/boolean/none) nie mają menu — zwracamy zestaw
 * tekstowy jako bezpieczny fallback (parytet z popupem `getOperatorsForCategory`),
 * ale dispatcher (`FilterRowCell`) i tak nie renderuje dla nich menu.
 */
export function getRowOperators(category: ColumnFilterCategory): RowFilterOperator[] {
  switch (category) {
    case 'number':
    case 'date':
      return ORDINAL_OPERATORS;
    default:
      return TEXT_OPERATORS;
  }
}

/**
 * Domyślny operator per kategoria. Tekst → `contains`; liczby i daty → `eq`
 * (jak zaznaczony domyślny „Equals" w DevExtreme). UWAGA: różni się od popupu,
 * którego zakładka „Warunek" domyślnie ustawia `contains` dla dat — to świadoma
 * rozbieżność, bo wiersz naśladuje DevExtreme, a popup zostaje jak był.
 */
export function getDefaultOperator(category: ColumnFilterCategory): RowFilterOperator {
  return category === 'number' || category === 'date' ? 'eq' : 'contains';
}

export function isRangeOperator(op: RowFilterOperator): boolean {
  return op === 'between';
}

export function isNoValueOperator(op: RowFilterOperator): boolean {
  return NO_VALUE_OPERATORS.includes(op);
}

/**
 * Klucz i18n etykiety operatora (wspólny dla menu wiersza i ewentualnie popupu). Parametr `string`
 * (nie sama unia `RowFilterOperator`), bo woła go też „echo" wygaszonej komórki grupy OR z
 * `ColumnHeaderFilter.operator` (typ `string`) — namespace `ops.*` mapuje każdy operator.
 */
export function operatorI18nKey(op: string): string {
  return `ui.configurableTable.filterRow.ops.${op}`;
}

/**
 * Odczyt aktualnego operatora z zapisanego filtra — do podświetlenia pozycji
 * w menu. `mode:'range'` → `between`; `mode:'condition'` → jego operator;
 * `mode:'values'` lub brak filtra → operator domyślny (tryb „wartości"
 * obsługuje osobny chip, nie menu).
 */
export function readOperatorFromFilter(
  filter: ColumnHeaderFilter | undefined,
  category: ColumnFilterCategory,
): RowFilterOperator {
  if (!filter) return getDefaultOperator(category);
  if (filter.mode === 'range') return 'between';
  if (filter.mode === 'condition' && filter.operator) {
    return filter.operator as RowFilterOperator;
  }
  return getDefaultOperator(category);
}

export interface BuildFilterInput {
  /** Identyfikator kolumny — trafia do `field` (serializer strony przemapuje na fieldSlug). */
  field: string;
  operator: RowFilterOperator;
  /** Wartość dla trybu warunku (ignorowana dla `between` i operatorów bez wartości). */
  value?: string;
  /** Granice dla `between` (`mode:'range'`). */
  rangeMin?: string;
  rangeMax?: string;
}

/**
 * Buduje `ColumnHeaderFilter` z wartości edytora wiersza — albo `null`, gdy filtr
 * jest pusty (i wpis w `headerFilters` należy usunąć; sam wybór operatora bez
 * wartości NIE tworzy filtra). Kształt 1:1 z `ColumnHeaderFilterPopup.handleApply`.
 */
export function buildFilterFromEditor(input: BuildFilterInput): ColumnHeaderFilter | null {
  const { field, operator } = input;

  if (isRangeOperator(operator)) {
    const min = (input.rangeMin ?? '').trim();
    const max = (input.rangeMax ?? '').trim();
    if (!min && !max) return null;
    return {
      field,
      mode: 'range',
      rangeMin: min || undefined,
      rangeMax: max || undefined,
    };
  }

  if (isNoValueOperator(operator)) {
    return { field, mode: 'condition', operator };
  }

  const value = input.value ?? '';
  if (value.trim() === '') return null;
  return { field, mode: 'condition', operator, conditionValue: value };
}
